import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/store/authStore';
import { useCartStore } from '@/store/cartStore';
import { parkCartForRecovery } from '@/lib/utils/session';
import { MAX_SESSION_MS, SESSION_WARNING_LEAD_MS } from '@/hooks/useIdleLogout';
import { ROUTES } from '@/lib/config/routes';

function countdown(ms: number) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

export function SessionExpiryWarning() {
  const navigate = useNavigate();
  const sessionStartedAt = useAuthStore((s) => s.sessionStartedAt);
  const hasItems = useCartStore((s) => s.items.length > 0);
  const [remaining, setRemaining] = useState<number | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!sessionStartedAt) {
      setRemaining(null);
      return;
    }
    setDismissed(false);
    const tick = () => {
      const left = MAX_SESSION_MS - (Date.now() - sessionStartedAt);
      setRemaining(left > 0 && left <= SESSION_WARNING_LEAD_MS ? left : null);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [sessionStartedAt]);

  if (remaining === null || dismissed) return null;

  const signOutNow = () => {
    parkCartForRecovery();
    useAuthStore.getState().clearSession();
    useCartStore.getState().clear();
    navigate(ROUTES.login, { replace: true });
  };

  return (
    <Modal
      open
      onClose={() => setDismissed(true)}
      title="Session ending soon"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setDismissed(true)}>
            Keep working
          </Button>
          <Button onClick={signOutNow}>Sign out &amp; sign in</Button>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="flex items-center gap-3 rounded-lg bg-amber-50 px-3 py-2.5 dark:bg-amber-900/30">
          <Clock className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
          <span className="text-sm text-amber-700 dark:text-amber-300">
            Your session ends in{' '}
            <span className="font-bold tabular-nums">{countdown(remaining)}</span>.
          </span>
        </div>
        <p className="text-sm text-content-muted">
          For security, sessions end automatically after 8 hours and this can&apos;t be extended.
          Finish or park your current sale, then sign in again to keep working.
        </p>
        {hasItems && (
          <p className="text-sm text-content-muted">
            Your current sale will be saved to{' '}
            <span className="font-medium text-content">Held Sales</span> so nothing is lost.
          </p>
        )}
      </div>
    </Modal>
  );
}
